import { observer } from "mobx-react-lite";
import { Info } from "lucide-react";
import { LoadingBar, PaginationControls } from "@/components/common";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import OrgDetailRow from "./OrgDetailRow";
import MetricDetailRowSkeleton from "./skeleton/MetricDetailRowSkeleton";

const OrgDetailPanel = observer(({ store, formatFrameworkLabel }) => {
  const { t } = useTran();
  const {
    rows,
    total,
    page,
    pageSize,
    loading,
    hasLoadedOnce,
    parentLevelMetrics,
    setPage,
    setPageSize,
  } = store;

  const showSkeleton = !hasLoadedOnce && loading;
  const showRefresh = hasLoadedOnce && loading;

  return (
    <div className="relative mb-3 mt-1 overflow-hidden rounded-md border border-border/40 bg-muted/20 p-3">
      {showRefresh && <LoadingBar />}
      <div
        className={cn(
          "space-y-3 transition-opacity",
          showRefresh && "opacity-60",
        )}
      >
        <PaginationControls
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[5, 10, 20]}
          disabled={loading}
          listLabel={t("Metrics")}
          hideWhenSinglePage
        />
        {parentLevelMetrics?.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-900 dark:text-amber-100">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              <span className="font-semibold">
                {parentLevelMetrics.map((m) => m.code).join(", ")}
              </span>{" "}
              {t(
                `${parentLevelMetrics.length > 1 ? "are" : "is"} presented at group level only.`,
              )}
            </span>
          </div>
        )}
        {showSkeleton ? (
          <div className="space-y-2">
            {Array.from({ length: pageSize }).map((_, i) => (
              <MetricDetailRowSkeleton key={i} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-border/70 p-6 text-center text-sm text-muted-foreground">
            {t("No metrics match your filters.")}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((row) => (
              <OrgDetailRow
                key={
                  row.year != null
                    ? `${row.disclosureId}-${row.year}`
                    : row.disclosureId
                }
                row={row}
                formatFrameworkLabel={formatFrameworkLabel}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
});

export default OrgDetailPanel;
